import { Link } from 'react-router-dom'
import {
  formatBytesShort,
  getEffectiveStorageLimitBytes,
  getPlanLabel,
  getStorageLevel,
  getStorageUsagePercent,
} from '../lib/plan-quotas.js'

type Plan = 'FREE' | 'PRO' | 'EPHEMERAL'

type Props = {
  plan: Plan
  addonStorageTb: number
  /** API は bigint を文字列で返すため string で受け取る */
  storageUsedBytes: string
  /** サイドバー向けのコンパクト表示 */
  compact?: boolean
}

/**
 * ストレージ使用量メーター（参考デザインの "ストレージ使用量" カードを踏襲）。
 *
 * - 70% 以上で黄色、90% 以上で赤＋アップグレード導線。
 * - PRO は上限が緩いので警告条件には到達しにくいが、addon の超過も同じ判定で出す。
 */
export function StorageMeter({ plan, addonStorageTb, storageUsedBytes, compact }: Props) {
  const used = Number(storageUsedBytes)
  const limit = getEffectiveStorageLimitBytes(plan, addonStorageTb)
  const percent = getStorageUsagePercent(used, limit)
  const level = getStorageLevel(percent)

  const fillClass =
    level === 'danger'
      ? 'bg-destructive'
      : level === 'warning'
        ? 'bg-amber-400'
        : 'bg-gradient-primary'

  if (compact) {
    return (
      <section className="rounded-md border border-border bg-muted/30 p-2.5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
            ストレージ ({getPlanLabel(plan)})
          </p>
          <p className="text-[10px] text-muted-foreground editorial-num">{percent}%</p>
        </div>
        {/* biome-ignore lint/a11y/useFocusableInteractive: progressbar はキーボードフォーカス対象外（WAI-ARIA 仕様） */}
        <div
          className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="ストレージ使用率"
        >
          <div
            className={`h-full rounded-full transition-[width] duration-700 ${fillClass}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="mt-1.5 text-[10px] text-muted-foreground editorial-num">
          {formatBytesShort(used)} / {formatBytesShort(limit)}
        </p>
      </section>
    )
  }

  return (
    <section className="bg-card border border-border rounded-lg p-5 shadow-sm">
      <header className="flex items-center justify-between gap-4 mb-3">
        <h2 className="text-sm font-bold text-foreground">
          ストレージ使用量{' '}
          <span className="text-muted-foreground font-medium">({getPlanLabel(plan)})</span>
        </h2>
        <p className="text-sm text-muted-foreground font-medium editorial-num">
          <span className="text-foreground font-semibold">{formatBytesShort(used)}</span>
          {' / '}
          {formatBytesShort(limit)}
          <span className="ml-2">({percent}%)</span>
        </p>
      </header>

      {/* biome-ignore lint/a11y/useFocusableInteractive: progressbar はキーボードフォーカス対象外（WAI-ARIA 仕様） */}
      <div
        className="h-3 w-full rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="ストレージ使用率"
      >
        <div
          className={`h-full rounded-full transition-[width] duration-700 ${fillClass}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {level !== 'ok' && plan === 'FREE' && (
        <p
          className={`mt-3 text-xs font-medium flex items-center gap-1.5 ${
            level === 'danger' ? 'text-destructive' : 'text-amber-700'
          }`}
        >
          <span aria-hidden>⚠</span>
          容量の上限が近づいています。
          <Link to="/upgrade" className="underline underline-offset-2 hover:opacity-80">
            プロプランへのアップグレード
          </Link>
          をご検討ください。
        </p>
      )}
    </section>
  )
}
